import React, { useState, useEffect } from 'react';
import {
    DndContext,
    closestCenter,
    useSensor,
    useSensors,
    PointerSensor,
    KeyboardSensor,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
    sortableKeyboardCoordinates,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useSnackbar } from 'notistack';
import {
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    LinearProgress,
    Typography, Switch, Button,
} from '@mui/material';
import DragIndicatorRoundedIcon from '@mui/icons-material/DragIndicatorRounded';
import axios from 'axios';
import { connect } from 'trim-redux';
import { defaultVariables } from '../../../../../config';
import AppCardLoaderFields from '../../../../components/loaders/AppCardLoaderFields';
import Grid from '@mui/material/Grid2';
import {useTranslation} from "react-i18next";
import {useParams} from "react-router";
import MakeMenuButton from "./MakeMenuButton";
import GetConfirmationButton from "../../../../components/elements/GetConfirmationButton";
import {CheckBoxRounded, DeleteForeverOutlined} from "@mui/icons-material";
import clsx from "clsx";
import {withStyles} from "@mui/styles";
import {Styles} from "../../../../../config/design/styles";
import {useNavigate} from "react-router-dom";
import Title from "../../../../components/elements/Title";
import {PackageOpen, Trash} from "lucide-react";
import NothingFound from "../../../../components/elements/NothingFound";
import {Alert} from "@mui/lab";
import {BadgeInfo} from "lucide-react";

const SortableItem = ({ id, item,reload,dragEnabled }) => {
    const { attributes, listeners, setNodeRef, transform, transition } =
        useSortable({ id });
    const [loading, setLoading] = useState(false);
    const { t } = useTranslation();

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };
    const loginToken = localStorage.getItem('ACT');
    const config = {
        headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${loginToken}`
        }
    }
    const handleDelete = (item_id) =>{
        setLoading(true);
        let data = new FormData();
        data.append('id', item_id);
        axios.post(`${defaultVariables.url}/menus/delete`,data,config)
            .then(response => {
                setLoading(false);
                reload();

            })
            .catch(error => {
                console.error("Error fetching item data:", error);
                setLoading(false);
            });
    }
    const handleActive = (item_id) =>{
        let data = new FormData();
        data.append('id', item_id);
        axios.post(`${defaultVariables.url}/menus/toggle-active`,data,config)
            .then(response => {
                reload();
            })
            .catch(error => {
                console.error("Error fetching item data:", error);

            });
    }
    return (
        <ListItem
            className={clsx("menu-list-item",!!dragEnabled && "draggable-com " ,!item.active && "disabled")}
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            secondaryAction={
                <>
                    {
                        !dragEnabled &&
                        <div style={{display:"flex",alignItems:'center',gap:5}}>
                            <Switch
                                size="small"
                                color="secondary"
                                checked={item.active}
                                onChange={(e) => {
                                    handleActive(id);
                                }}
                            />
                            <GetConfirmationButton
                                handleAction={() => handleDelete(item.id)}
                                question={t('doYouWanaDeleteItem')}
                                icon={<CheckBoxRounded/>}
                                tooltip={t('delete')}
                                loading={loading}
                            >
                                <Trash className="lucide-icon-primary btn"/>
                            </GetConfirmationButton>
                            <MakeMenuButton item_id={item.id} reload={reload}/>

                        </div>

                    }
                </>
            }
        >
            {
                !!dragEnabled &&
                    <ListItemIcon>
                        <DragIndicatorRoundedIcon color="secondary" />
                    </ListItemIcon>
            }
            <ListItemText
                primary={<Typography>{item.name}</Typography>}
            />

        </ListItem>
    );
};

const ShopMenus = ({ create, user ,isApp}) => {
    const [variables, setVariables] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dragLoading, setDragLoading] = useState(false);
    const [dragEnabled, setDragEnabled] = useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const navigation = useNavigate();

    let { id } = useParams();
    if (!id && !create) {
        if (!!user.shop) {
            id = user.shop.id;
        } else {
            navigation('/');
        }
    }

    useEffect(() => {
        loadData();
    }, [id]);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const loadData = async () => {
        const loginToken = localStorage.getItem('ACT');
        if (!loginToken) return;

        try {
            const config = {
                headers: {
                    Accept: 'application/json',
                    Authorization: `Bearer ${loginToken}`,
                },
            };
            const response = await axios.get(`${defaultVariables.url}/menus/get-all/${id}`, config);
            setVariables(response.data);
        } catch (error) {
            console.error(error.response);
            enqueueSnackbar(t('errorLoadingData'), { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleDragStart = () => {
        document.body.style.overflow = 'hidden'; // Disable scrolling
    };

    const handleDragEnd = async ({ active, over }) => {
        document.body.style.overflow = ''; // Re-enable scrolling

        if (!over || active.id === over.id) return;

        const oldIndex = variables.findIndex((item) => item.id === active.id);
        const newIndex = variables.findIndex((item) => item.id === over.id);

        setDragLoading(true);
        setVariables((prev) => arrayMove(prev, oldIndex, newIndex));

        const data = new FormData();
        data.append('source', active.id);
        data.append('destination', over.id);
        try {
            const loginToken = localStorage.getItem('ACT');
            const config = {
                headers: {
                    Accept: 'application/json',
                    Authorization: `Bearer ${loginToken}`,
                },
            };
            await axios.post(`${defaultVariables.url}/menus/changeOrder`, data, config);
        } catch (error) {
            console.error(error.response);
        } finally {
            setDragLoading(false);
        }
    };

    return (
        <>
            <Grid container spacing={2} justifyContent="center" alignItems="center" className="drag-con">
                {
                    !isApp &&
                    <Grid item size={12}>
                        <Title align="left" color="text" className="panel-title">{t('menus')}</Title>
                    </Grid>
                }
                <Grid item size={12}>
                    <Alert severity="success"  icon={<BadgeInfo  className="lucide-large"/>}>
                        {t('shopMenuHint')}
                    </Alert>
                </Grid>
                {!!loading ? (
                    <Grid item size={12}>
                        <AppCardLoaderFields />
                    </Grid>
                ) : (
                    <>
                        <Grid item size={12}>
                            <MakeMenuButton reload={loadData} shop_id={id} />
                            <Button
                                style={{ margin: "0 5px" }}
                                size="small"
                                color="secondary"
                                disabled={variables.length < 2}
                                variant={!!dragEnabled ? "contained" : "outlined"}
                                onClick={() => setDragEnabled(!dragEnabled)}
                            >
                                {dragEnabled ? t('disableOrdering') : t('enableOrdering')}
                            </Button>
                        </Grid>
                        <Grid item size={12}>
                            {dragLoading && <LinearProgress />}
                        </Grid>
                        <Grid item size={12}>
                            {variables.length > 0 ? (
                                <>
                                    {!!dragEnabled ? (
                                        <DndContext
                                            sensors={sensors}
                                            collisionDetection={closestCenter}
                                            onDragStart={handleDragStart}
                                            onDragEnd={handleDragEnd}
                                        >
                                            <SortableContext
                                                items={variables.map((item) => item.id)}
                                                strategy={verticalListSortingStrategy}
                                            >
                                                <List>
                                                    {variables.map((item) => (
                                                        <SortableItem
                                                            key={item.id}
                                                            id={item.id}
                                                            item={item}
                                                            reload={loadData}
                                                            dragEnabled={dragEnabled}
                                                        />
                                                    ))}
                                                </List>
                                            </SortableContext>
                                        </DndContext>
                                    ) : (
                                        <List>
                                            {variables.map((item) => (
                                                <SortableItem
                                                    key={item.id}
                                                    id={item.id}
                                                    item={item}
                                                    reload={loadData}
                                                    dragEnabled={dragEnabled}
                                                />
                                            ))}
                                        </List>
                                    )}
                                </>
                            ) : (
                                <NothingFound
                                    type="page"
                                    title={t('noItemsFound')}
                                    title_color="text"
                                    paragraph={t('tryAddSomeItemsToMenu')}
                                    icon={<PackageOpen />}
                                />
                            )}
                        </Grid>
                    </>
                )}
            </Grid>
        </>
    );
};

const mstp = (state) => ({
    isAuth: state.isAuth,
    user: state.user,
    isApp: state.isApp,
});

export default withStyles(Styles)(connect(mstp)(ShopMenus));

